import { SpoolDto } from '../../types/spool-local';
import { useWeb3React } from '@web3-react/core';
import { useContext } from 'react';
import useSWR from 'swr';
import { IncentivizeSpoolTypes } from '../../../config/constants';
import appContext from '../../../context/app/appContext';
import { sleep } from '../../../web3/utils';

const INCENTIVIZED_REWARDS_FAKE_TOKENS = ['0xece2b4cb29cf6ee46f52cbdcc17890f60b6bbf07'];

const useSpoolsCreatedByUser = () => {
    const { account, chainId } = useWeb3React();

    const { sdk } = useContext(appContext);

    const { data, error, mutate } = useSWR(
        sdk && account ? ['spoolsCreatedByUser', account, chainId] : null,
        async (_, userAddress) => {
            const spools = await sdk!.spools.getSpoolsCreatedByUser(userAddress);
            return spools.map((spool) => ({
                ...spool,
                incentivizedRewards: spool.incentivizedRewards.filter(
                    (incentivizedReward) =>
                        !INCENTIVIZED_REWARDS_FAKE_TOKENS.includes(
                            incentivizedReward.id.toLowerCase()
                        )
                ),
            }));
        }
    );

    const updateOnCreate = async (originalLength: number | undefined) => {
        try {
            let retries = 0;
            let spoolsCreated: SpoolDto[] | undefined;

            do {
                retries++;
                spoolsCreated = await mutate();

                if (spoolsCreated?.length !== originalLength) {
                    break;
                }

                await sleep(3000);
            } while (retries < 5);

            if (!spoolsCreated) return;

            await mutate(() => spoolsCreated, false);

            return spoolsCreated.sort((a, b) => +b.dateCreated - +a.dateCreated)[0];
        } catch (error) {
            console.log(error);
        }
    };

    const updateOnIncentivize = async (
        spool: SpoolDto,
        updateType: IncentivizeSpoolTypes | 'ADD_INCENTIVE' | 'REMOVE_INCENTIVE',
        incentiveAddress: string
    ) => {
        // emissionStartDate;

        if (!sdk || !account) return;

        let retries = 0;
        let userCreatedSpool: SpoolDto | undefined;

        do {
            retries++;
            userCreatedSpool = await sdk.spools.getSpoolById(spool.address);

            if (
                !spool.incentivizedRewards.length &&
                userCreatedSpool?.incentivizedRewards.length
            ) {
                break;
            }

            if (
                (updateType === 'ADD_INCENTIVE' || updateType === 'REMOVE_INCENTIVE') &&
                spool.incentivizedRewards.length !==
                    userCreatedSpool?.incentivizedRewards.length
            ) {
                break;
            }

            if (updateType !== 'ADD_INCENTIVE') {
                const previousIncentive = spool.incentivizedRewards.find(
                    ({ id }) => id === incentiveAddress
                );
                const currentIncentive = userCreatedSpool?.incentivizedRewards.find(
                    ({ id }) => id === incentiveAddress
                );

                if (
                    previousIncentive?.emissionStartDate !==
                    currentIncentive?.emissionStartDate
                ) {
                    break;
                }
            }

            await sleep(3000);
        } while (retries < 5);

        if (!userCreatedSpool) return;

        await mutate(async (spools) => {
            const filteredSpools = spools
                ? spools.filter((spool) => spool.address !== userCreatedSpool!.address)
                : [];

            return [...filteredSpools, userCreatedSpool!];
        }, false);
    };

    return {
        spoolsCreatedByUser: data,
        isLoading: !error && !data,
        error,
        updateOnIncentivize,
        updateOnCreate,
    };
};

export default useSpoolsCreatedByUser;
